import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiSend, FiChevronLeft, FiChevronRight, FiClock, FiCheckCircle, FiXCircle, FiBell } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../contexts/ToastContext';
import TriggerReminderModal from '../components/TriggerReminderModal';
import apiClient, { endpoints } from '../api/client';

const Reminders = () => {
  const { isLimitedAdmin } = useAuth();
  const { showError, showSuccess } = useToast();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    due_days: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalReminders: 0,
    hasNext: false,
    hasPrev: false,
    limit: 10
  });
  const [resendingId, setResendingId] = useState(null);
  const [showTriggerModal, setShowTriggerModal] = useState(false);

  const fetchReminders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit
      });

      if (searchTerm) params.append('search', searchTerm);
      if (filters.status) params.append('status', filters.status);
      if (filters.due_days) params.append('due_days', filters.due_days);

      const response = await apiClient.get(`${API_ENDPOINTS.DASHBOARD_REMINDERS}?${params}`);
      
      if (response.data.success) {
        setReminders(response.data.data.reminders);
        setPagination(prev => ({
          ...prev,
          ...response.data.data.pagination
        }));
      }
    } catch (err) {
      console.error('Error fetching reminders:', err);
      showError('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReminders();
  }, [pagination.currentPage, pagination.limit, searchTerm, filters.status, filters.due_days]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleTriggerReminder = async (reminderId) => {
    if (isLimitedAdmin()) {
      showError('You do not have permission to trigger reminders.');
      return;
    }

    try {
      setResendingId(reminderId);

      const response = await apiClient.post(`/api/reminders/send/${reminderId}`);

      if (response.data.success) {
        showSuccess('Reminder triggered successfully');
        fetchReminders(); // Refresh the list
      } else {
        showError(response.data.message || 'Failed to trigger reminder');
      }
    } catch (err) {
      console.error('Error triggering reminder:', err);
      showError('Failed to trigger reminder');
    } finally {
      setResendingId(null);
    }
  };

  const handleResendReminder = async (reminderId) => {
    if (isLimitedAdmin()) {
      showError('You do not have permission to resend reminders');
      return;
    }

    try {
      setResendingId(reminderId);

      const response = await apiClient.post(API_ENDPOINTS.REMINDER_RESEND(reminderId));
      
      if (response.data.success) {
        showSuccess('Reminder sent successfully');
        fetchReminders(); // Refresh the list
      } else {
        showError(response.data.message || 'Failed to resend reminder');
      }
    } catch (err) {
      console.error('Error resending reminder:', err);
      showError('Failed to resend reminder');
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        icon: FiClock,
        classes: 'bg-yellow-100 text-yellow-800',
        text: 'Pending'
      },
      sent: {
        icon: FiCheckCircle,
        classes: 'bg-green-100 text-green-800',
        text: 'Sent'
      },
      failed: {
        icon: FiXCircle,
        classes: 'bg-red-100 text-red-800',
        text: 'Failed'
      }
    };

    const config = statusConfig[status] || {
      icon: FiClock,
      classes: 'bg-gray-100 text-gray-800',
      text: status
    };

    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.classes}`}>
        <Icon className="mr-1 h-3 w-3" />
        {config.text}
      </span>
    );
  };

  const getDueDateStatus = (dueDate, status) => {
    if (!dueDate) return null;

    if (status === 'sent') {
      return <span className="text-green-600 font-medium">Sent</span>;
    }
    
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return <span className="text-red-600 font-medium">Overdue by {Math.abs(diffDays)} days</span>;
    } else if (diffDays === 0) {
      return <span className="text-yellow-600 font-medium">Due today</span>;
    } else if (diffDays <= 7) {
      return <span className="text-yellow-600">Due in {diffDays} days</span>;
    } else {
      return <span className="text-green-600">Due in {diffDays} days</span>;
    }
  };

  const handleReminderSent = () => {
    fetchReminders(); // Refresh the list
    setShowTriggerModal(false);
  };



  return (
    <div className="page-content">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reminders</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage certificate expiry reminders
            </p>
          </div>
          {/* <div className="mt-4 sm:mt-0">
            <button
              onClick={() => setShowTriggerModal(true)}
              disabled={isLimitedAdmin()}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed btn-animate transition-all duration-200"
              title={isLimitedAdmin() ? 'You do not have permission to trigger reminders' : 'Trigger a manual reminder'}
            >
              <FiBell className="-ml-1 mr-2 h-4 w-4" />
              Trigger Reminder Manually
            </button>
          </div> */}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-5">
        <div className="flex">
          <FiBell className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Reminders
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Review and manage reminder schedules for all certificates. Manually send or adjust notifications as needed.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search reminders..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>

            {/* Due Days Filter */}
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
              value={filters.due_days}
              onChange={(e) => handleFilterChange('due_days', e.target.value)}
            >
              <option value="">All Due</option>
              <option value="7">Due in 7 days</option>
              <option value="30">Due in 30 days</option>
              <option value="60">Due in 60 days</option>
              <option value="90">Due in 90 days</option>
            </select>
          </div>
        </div>
      </div>



      {/* Reminders Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Reminders ({pagination.totalReminders})
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary btn-pulse"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User & Certificate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Sent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reminders.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      No reminders found
                    </td>
                  </tr>
                ) : (
                  reminders.map((reminder) => (
                    <tr key={reminder.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {reminder.user?.first_name || 'N/A'} {reminder.user?.last_name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {reminder.user_email || ''}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            <span className="font-medium">Certificate:</span> {reminder.certificate?.type || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(reminder.scheduled_at)}
                        </div>
                        <div className="text-sm">
                          {getDueDateStatus(reminder.scheduled_at, reminder.status)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(reminder.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDateTime(reminder.sent_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {(!isLimitedAdmin() && reminder.status !== "pending") && (
                          <button
                            onClick={() => handleResendReminder(reminder.id)}
                            disabled={resendingId === reminder.id}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-lg text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed btn-animate transition-all duration-200"
                          >
                            {resendingId === reminder.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                            ) : (
                              <FiSend className="mr-1 h-4 w-4" />
                            )}
                            {resendingId === reminder.id ? 'Sending...' : 'Resend'}
                          </button>
                        )}
                        {(!isLimitedAdmin() && reminder.status === "pending") && (
                          <button
                            onClick={() => handleTriggerReminder(reminder.certificate?.id)}
                            disabled={resendingId === reminder.id}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-lg text-white bg-green-500 hover:bg-green-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed btn-animate transition-all duration-200"
                          >
                            {resendingId === reminder.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                            ) : (
                              <FiSend className="mr-1 h-4 w-4" />
                            )}
                            {resendingId === reminder.id ? 'Sending...' : 'Trigger'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrev}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed btn-animate transition-all duration-200"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed btn-animate transition-all duration-200"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{pagination.currentPage}</span> of{' '}
                  <span className="font-medium">{pagination.totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-lg border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed btn-animate transition-all duration-200"
                  >
                    <FiChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-lg border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed btn-animate transition-all duration-200"
                  >
                    <FiChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trigger Reminder Modal */}
      {showTriggerModal && (
        <TriggerReminderModal
          isOpen={showTriggerModal}
          onClose={() => setShowTriggerModal(false)}
          onReminderSent={handleReminderSent}
        />
      )}
    </div>
  );
};

export default Reminders;